// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from "../config/api";
import "./EditProfile.css";
import SidebarAd from "../Blog/SidebarAd";

export default function EditProfile() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const [userData, setUserData] = useState(() => {
    const localUser = sessionStorage.getItem("user");
    return localUser
      ? JSON.parse(localUser)
      : { name: "", email: "", profileImage: "", isSubscribed: false };
  });

  const [originalData, setOriginalData] = useState(userData);
  const [editableFields, setEditableFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subHistory, setSubHistory] = useState([]);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Collapsible sections — only one open at a time
  const [openSection, setOpenSection] = useState("account");

  const toggleSection = (key) => {
    setOpenSection((prev) => (prev === key ? null : key));
  };

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      navigate("/signin", { state: { from: "/edit-profile" } });
    }
  }, [token, navigate]);

  // Fetch user data
  useEffect(() => {
    if (!token) return;

    axios
      .get(API_ENDPOINTS.USER.PROFILE, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUserData(res.data);
        setOriginalData(res.data);
        setLoading(false);
      })
      .catch(() => {
        const localUser = sessionStorage.getItem("user");
        if (localUser) {
          setUserData(JSON.parse(localUser));
          setOriginalData(JSON.parse(localUser));
        }
        setLoading(false);
      });
  }, [token]);

  // Fetch subscription status and history
  useEffect(() => {
    if (!token) return;

    axios
      .get(API_ENDPOINTS.SUBSCRIPTION.STATUS, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data && res.data.active) {
          setSubscription(res.data);
        }
      })
      .catch(() => {});

    axios
      .get(API_ENDPOINTS.SUBSCRIPTION.HISTORY, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data && res.data.success) {
          setSubHistory(res.data.data || []);
        }
      })
      .catch(() => {});
  }, [token]);

  const enableEdit = (field) => {
    setEditableFields((prev) => ({ ...prev, [field]: true }));
    setSaveMessage(null);
  };

  const cancelEdit = (field) => {
    setUserData((prev) => ({ ...prev, [field]: originalData[field] }));
    setEditableFields((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const hasChanges = Object.keys(editableFields).some(
    (key) => userData[key] !== originalData[key]
  );

  const handleSave = () => {
    setSaving(true);
    setSaveMessage(null);

    axios
      .put(API_ENDPOINTS.USER.PROFILE, userData, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        const updatedUser = {
          ...JSON.parse(sessionStorage.getItem("user")),
          name: userData.name,
          profileImage: userData.profileImage,
          isSubscribed: userData.isSubscribed,
        };
        sessionStorage.setItem("user", JSON.stringify(updatedUser));

        setEditableFields({});
        setOriginalData(userData);
        setSaveMessage({ type: "success", text: "Profile updated successfully!" });

        if (window.updateHeaderUser) window.updateHeaderUser(updatedUser);
        window.dispatchEvent(new Event("storage"));
        window.dispatchEvent(new CustomEvent("userUpdated", { detail: updatedUser }));
      })
      .catch(() => {
        setSaveMessage({ type: "error", text: "Update failed. Please try again." });
      })
      .finally(() => setSaving(false));
  };

  const handleCancelSubscription = () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will still have access until the end of the billing period.")) return;

    setCancellingSubscription(true);
    axios
      .post(
        API_ENDPOINTS.SUBSCRIPTION.CANCEL,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setSubscription(null);
        setSaveMessage({ type: "success", text: "Subscription cancelled successfully." });
      })
      .catch(() => {
        setSaveMessage({ type: "error", text: "Failed to cancel subscription." });
      })
      .finally(() => setCancellingSubscription(false));
  };

  const handleLogout = () => {
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("token");
    window.location.href = "/signin";
  };

  if (loading)
    return (
      <div className="loader-overlay">
        <span className="loader"></span>
      </div>
    );

  const userInitial = userData.name ? userData.name.charAt(0).toUpperCase() : "?";
  const memberSince = userData.createdAt
    ? new Date(userData.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long" })
    : null;

  return (
    <div className="ep-layout mx-auto grid w-full max-w-7xl gap-4 px-3 py-4 lg:grid-cols-[280px_1fr]">
      {/* Sidebar */}
      <aside className="ep-sidebar rounded-2xl bg-white/90 p-4 shadow-sm">
        <div className="ep-sidebar-group">
          <p className="ep-sidebar-label">User Settings</p>
          <button className={`ep-sidebar-item ${openSection === "account" ? "active" : ""}`} onClick={() => toggleSection("account")}>
            <i className="fi fi-sr-user"></i> My Account
          </button>
          <button className={`ep-sidebar-item ${openSection === "emailPref" ? "active" : ""}`} onClick={() => toggleSection("emailPref")}>
            <i className="fi fi-sr-envelope"></i> Email Preferences
          </button>
        </div>

        <div className="ep-sidebar-group">
          <p className="ep-sidebar-label">Billing</p>
          <button className={`ep-sidebar-item ${openSection === "subscription" ? "active" : ""}`} onClick={() => toggleSection("subscription")}>
            <i className="fi fi-sr-credit-card"></i> Subscription
          </button>
          <button className={`ep-sidebar-item ${openSection === "history" ? "active" : ""}`} onClick={() => toggleSection("history")}>
            <i className="fi fi-sr-time-past"></i> Billing History
          </button>
        </div>

        <div className="ep-sidebar-group">
          <p className="ep-sidebar-label">Actions</p>
          <button className="ep-sidebar-item ep-logout-item" onClick={handleLogout}>
            <i className="fi fi-sr-sign-out-alt"></i> Log Out
          </button>
        </div>

        {/* Desktop Ad - Shown only on laptop/tablet landscape */}
        <div className="ep-sidebar-ad-container">
          <SidebarAd />
        </div>
      </aside>

      {/* Main Content */}
      <main className="ep-main space-y-4 rounded-2xl bg-white/90 p-3 shadow-sm md:p-4">
        {/* Save bar */}
        {(hasChanges || saveMessage) && (
          <div className={`ep-save-bar ${saveMessage ? (saveMessage.type === "success" ? "ep-save-success" : "ep-save-error") : ""}`}>
            {saveMessage ? (
              <span>{saveMessage.text}</span>
            ) : (
              <>
                <span>You have unsaved changes</span>
                <div className="ep-save-actions">
                  <button className="ep-btn-text" onClick={() => { setUserData(originalData); setEditableFields({}); }}>Reset</button>
                  <button className="ep-btn-primary ep-btn-sm" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── My Account ── */}
        <section className="ep-section">
          <button className="ep-section-header" onClick={() => toggleSection("account")}>
            <h2>My Account</h2>
            <i className={`fi fi-rr-angle-small-down ep-chevron ${openSection === "account" ? "open" : ""}`}></i>
          </button>

          {openSection === "account" && (
            <div className="ep-section-body">
              {/* Profile Card */}
              <div className="ep-profile-card">
                <div className="ep-profile-banner"></div>
                <div className="ep-profile-info">
                  <div className="ep-avatar">{userInitial}</div>
                  <div className="ep-profile-meta">
                    <h3 className="ep-display-name">{userData.name}</h3>
                    {memberSince && <span className="ep-member-since">Member since {memberSince}</span>}
                  </div>
                </div>
              </div>

              {/* Fields */}
              <div className="ep-fields">
                <div className="ep-field-row">
                  <div className="ep-field-info">
                    <span className="ep-field-label">Display Name</span>
                    {editableFields.name ? (
                      <input className="ep-field-input" name="name" value={userData.name} onChange={handleChange} autoFocus />
                    ) : (
                      <span className="ep-field-value">{userData.name}</span>
                    )}
                  </div>
                  {editableFields.name ? (
                    <button className="ep-btn-text" onClick={() => cancelEdit("name")}>Cancel</button>
                  ) : (
                    <button className="ep-btn-secondary" onClick={() => enableEdit("name")}>Edit</button>
                  )}
                </div>

                <div className="ep-field-row">
                  <div className="ep-field-info">
                    <span className="ep-field-label">Email</span>
                    <span className="ep-field-value">{userData.email}</span>
                  </div>
                </div>

                {userData.role && (
                  <div className="ep-field-row">
                    <div className="ep-field-info">
                      <span className="ep-field-label">Role</span>
                      <span className="ep-field-value ep-capitalize">{userData.role}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {/* ── Email Preferences ── */}
        <section className="ep-section">
          <button className="ep-section-header" onClick={() => toggleSection("emailPref")}>
            <h2>Email Preferences</h2>
            <i className={`fi fi-rr-angle-small-down ep-chevron ${openSection === "emailPref" ? "open" : ""}`}></i>
          </button>

          {openSection === "emailPref" && (
            <div className="ep-section-body">
              <div className="ep-field-row">
                <div className="ep-field-info">
                  <span className="ep-field-label">Newsletter Subscription</span>
                  <span className="ep-field-desc">Receive email updates about new blog posts, podcasts, and features.</span>
                </div>
                <div className="ep-toggle-wrapper">
                  <input
                    type="checkbox"
                    id="ep-newsletter-toggle"
                    className="ep-toggle-input"
                    name="isSubscribed"
                    checked={userData.isSubscribed}
                    onChange={(e) => {
                      handleChange(e);
                      if (!editableFields.isSubscribed) enableEdit("isSubscribed");
                    }}
                  />
                  <label htmlFor="ep-newsletter-toggle" className="ep-toggle-label">
                    <span className="ep-toggle-track"></span>
                  </label>
                  <span className={`ep-toggle-status ${userData.isSubscribed ? "active" : ""}`}>
                    {userData.isSubscribed ? "Subscribed" : "Not subscribed"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* ── Subscription ── */}
        <section className="ep-section">
          <button className="ep-section-header" onClick={() => toggleSection("subscription")}>
            <h2>Subscription</h2>
            <i className={`fi fi-rr-angle-small-down ep-chevron ${openSection === "subscription" ? "open" : ""}`}></i>
          </button>

          {openSection === "subscription" && (
            <div className="ep-section-body">
              {subscription && subscription.active ? (
                <div className="ep-subscription-card">
                  <div className="ep-sub-header">
                    <div className="ep-sub-plan-info">
                      <span className="ep-sub-plan-name">{subscription.plan}</span>
                      <span className="ep-sub-status active">Active</span>
                    </div>
                  </div>
                  <div className="ep-sub-details">
                    {subscription.expiryDate && (
                      <div className="ep-sub-detail">
                        <span className="ep-field-label">Renews on</span>
                        <span className="ep-field-value">
                          {new Date(subscription.expiryDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                        </span>
                      </div>
                    )}
                    {subscription.amount != null && (
                      <div className="ep-sub-detail">
                        <span className="ep-field-label">Amount</span>
                        <span className="ep-field-value">
                          {subscription.currency === "INR" ? "₹" : subscription.currency === "EUR" ? "€" : "$"}
                          {subscription.amount}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="ep-sub-actions">
                    <Link to="/subscription" className="ep-btn-primary">Upgrade Plan</Link>
                    <button className="ep-btn-danger" onClick={handleCancelSubscription} disabled={cancellingSubscription}>
                      {cancellingSubscription ? "Cancelling..." : "Cancel Subscription"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="ep-subscription-empty">
                  <i className="fi fi-sr-diamond ep-empty-icon"></i>
                  <h3>No Active Subscription</h3>
                  <p>Upgrade to unlock premium content, remove ads, and access AI-powered apps.</p>
                  <Link to="/subscription" className="ep-btn-primary">View Plans</Link>
                </div>
              )}
            </div>
          )}
        </section>

        {/* ── Billing History ── */}
        <section className="ep-section">
          <button className="ep-section-header" onClick={() => toggleSection("history")}>
            <h2>Billing History</h2>
            <i className={`fi fi-rr-angle-small-down ep-chevron ${openSection === "history" ? "open" : ""}`}></i>
          </button>

          {openSection === "history" && (
            <div className="ep-section-body">
              {subHistory.length > 0 ? (
                <div className="ep-history-table-wrap">
                  <table className="ep-history-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subHistory.map((item, idx) => (
                        <tr key={idx}>
                          <td>{new Date(item.startDate || item.createdAt).toLocaleDateString()}</td>
                          <td className="ep-capitalize">{item.plan}</td>
                          <td>
                            {item.currency === "INR" ? "₹" : item.currency === "EUR" ? "€" : "$"}
                            {item.amount}
                          </td>
                          <td>
                            <span className={`ep-status-badge ${item.status}`}>{item.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="ep-empty-text">No billing history yet.</p>
              )}
            </div>
          )}
        </section>
      </main>

      {/* Mobile Ad - Shown only on mobile below ep-main */}
      <div className="ep-mobile-ad-container lg:hidden">
        <SidebarAd />
      </div>
    </div>
  );
}
